import { toast } from 'react-toastify';

/**
 * A NOTIFICATION TAPPED DURING A LIVE CALL (backlog 19b, David 2026-11-01).
 *
 * public/sw.js navigates the tab a browser notification click picks, and a
 * navigation loads a new document: a live 1:1 call or circle session in that
 * tab drops (or raises empathy.tsx's leave prompt). So before it navigates,
 * the worker asks the tab whether it is in a call (IN_CALL_QUERY, with a
 * MessageChannel port for the answer). A tab in one is not navigated: the
 * worker brings it forward and posts NOTIFICATION_TAP_MESSAGE with the
 * notification's title and the route the tap was for, the system
 * notification stays in the tray, and the call shows a short notice naming
 * where that lives. The notice is not a link: following it would end the call.
 *
 * ONE ANSWER, FROM THE CALL STORES. "In a call" is the 1:1 queue store's
 * `isCallSessionLive` (the predicate the pill and Back already ask) or the
 * circle session store's `isInSession`; nothing else keeps a copy. The worker
 * asks at the moment of the tap instead of holding a flag the page updates,
 * because a browser stops and restarts an idle worker and a flag would not
 * survive it.
 */

/** What sw.js asks a tab before it navigates it (keep in step with sw.js). */
export const IN_CALL_QUERY = 'empathease:in-call?';

/** What sw.js posts to a tab in a call instead of navigating it (keep in step with sw.js). */
export const NOTIFICATION_TAP_MESSAGE = 'empathease:notification-tap';

/** Long enough to read a sentence during a call. */
export const IN_CALL_TAP_NOTICE_MS = 6000;

/**
 * Is this tab in a live 1:1 call or circle session? Both stores are read
 * through dynamic imports: this runs from the boot listener (main.tsx), and
 * the circle store is not in the entry chunk. The 1:1 store is (so its
 * import settles at once), and it is asked first, so a live 1:1 call is
 * answered well inside the worker's wait even when the circle chunk has
 * never loaded (in which case there is no circle session either).
 */
export async function isInLiveCall(): Promise<boolean> {
  const queue = await import('../stores/empathy-queue.store');
  if (queue.isCallSessionLive(queue.useEmpathyQueueStore.getState())) return true;
  const circle = await import('../stores/circle-session.store');
  return circle.useCircleSessionStore.getState().isInSession;
}

/** Where a notification route lives, in the words the app uses for it. */
export function notificationTapDestination(route: string): string {
  const path = route.split('?')[0];
  if (/^\/app\/circles\/cohorts\/\d+/.test(path)) return "the group's page";
  if (/^\/app\/circles\/\d+$/.test(path)) return 'the session page';
  if (path === '/app/empathy') {
    return /[?&]tab=1on1(&|$)/.test(route) ? 'the 1-on-1 page' : 'the Circles page';
  }
  if (/^\/app\/bookings\/\d+$/.test(path)) return 'the booked call page';
  if (path === '/app/profile') return 'your Profile';
  if (path === '/app/wallet') return 'your Wallet';
  if (path === '/app/paths' || path === '/app') return 'the Course page';
  if (path === '/app/conversations') return 'your Conversations';
  return 'its page';
}

/** The notice: the notification's title, then where it lives. */
export function inCallTapNoticeText(title: string, route: string): string {
  const head = title.trim().replace(/[.!?:]+$/, '') || 'Notification';
  return `${head}. It's on ${notificationTapDestination(route)}: finish this call first.`;
}

/**
 * Show the notice over the call. Not clickable (no link, a tap does not
 * dismiss it either, so a thumb on the call screen does not lose it), and one
 * per destination: a second tap on the same notification while it is up adds
 * nothing.
 */
export function showInCallTapNotice(title: string, route: string): void {
  toast.info(inCallTapNoticeText(title, route), {
    toastId: `notification-tap:${route}`,
    autoClose: IN_CALL_TAP_NOTICE_MS,
    closeOnClick: false,
    closeButton: false,
    draggable: false,
  });
}
